import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Form, Formik } from "formik";
import TextBox from "../common/TextBox";
import ComboBox from "../common/ComboBox";
import Button from "../common/Button";
import NewProjectContext from "../../contexts/NewProjectContext";
import TextArea from "../common/TextArea";
import useUsers from "../../hooks/useUsers";
import { PAGE_STATE, langComponent } from "../../constants";
import AppContext from "../../AppContext";

import { ReactComponent as ArrowRightSvg } from "../../assets/icons/arrow-right.svg";
import { ReactComponent as ArrowLeftSvg } from "../../assets/icons/arrow-left.svg";
import programSketchUpImage from "../../assets/icons/programs/sketchUp.png";
import program3dsMaxImage from "../../assets/icons/programs/3dsMax.png";
import programRevitImage from "../../assets/icons/programs/autodeskRevit.png";
import programCinema4DImage from "../../assets/icons/programs/cinema4d.png";
import programUnrealImage from "../../assets/icons/programs/unreal.png";
import programAutocadImage from "../../assets/icons/programs/autocad.png";
import programArchicadImage from "../../assets/icons/programs/archicad.png";
import programRhinoImage from "../../assets/icons/programs/rhino.png";

import programVRayImage from "../../assets/icons/programs/vray.png";
import programCoronaImage from "../../assets/icons/programs/corona.png";
import programD5RenderImage from "../../assets/icons/programs/d5render.png";
import programEnscapeImage from "../../assets/icons/programs/enscape.jpg";
import programLumionImage from "../../assets/icons/programs/lumion.png";
import { isValidNumber } from "../../helpers/common.helper";
import { useLocation } from "react-router-dom";
// import SelectMusic from "./SelectMusic";

const Info = (props) => {
  const { details, setDetails, goForward, goBackward } =
    useContext(NewProjectContext);
  const { getLang, designStyles } = useContext(AppContext);
  const Translation = getLang(langComponent.project);
  const TranslationCommon = getLang(langComponent.common);
  const TranslationWorkType = getLang(langComponent.workType);
  const TranslationSpaceType = getLang(langComponent.spaceType);
  const TranslationStyleType = getLang(langComponent.styleType);
  const [works, setWorks] = useState(undefined);
  const [spaces, setSpaces] = useState(undefined);
  const [styles, setStyles] = useState(undefined);
  const [pageState, setPageState] = useState(PAGE_STATE.LOADING);
  let location = useLocation();
  const [info, setInfo] = useState(
    details?.info || {
      name: "",
      desc: "",
      price: "",
      area: "",
      program: "",
      renderer: "",
      work: "",
      space: "",
      style: [],
      location: "",
    }
  );

  useEffect(() => {
    init();

    return () => {};
  }, []);

  const init = async () => {
    setWorks(designStyles.works);
    setSpaces(designStyles.spaces);
    setStyles(designStyles.styles);

    setPageState(PAGE_STATE.IDLE);
  };
  const handleGoBack = () => {
    location.state = {};
    goBackward();
  };

  // * Handle form validate
  const handleValidate = ({
    name,
    desc,
    price,
    area,
    program,
    renderer,
    work,
    space,
    style,
  }) => {
    let errors = {};

    const _price = Number(price);

    if (!name) errors.name = Translation("Please name your project");
    if (!desc) errors.desc = Translation("Please add a description");

    if (details.type !== "images") {
      if (!program)
        errors.program = Translation("Please select the program used");
      if (!renderer)
        errors.renderer = Translation("Please select the rendering app used");
    }

    if (!work) errors.work = Translation("Please specify type of work");
    if (!space) errors.space = Translation("Please specify type of space");
    if (!style || style.length === 0)
      errors.style = Translation("Please specify type of styles");

    // * Validate price
    if (details.type === "panorama" && !isValidNumber(price))
      errors.price = Translation("Price can only be a positive number");
    if (details.type === "panorama" && (price === "" || price === undefined))
      delete errors.price;

    // * Validate area
    if (area && (!isValidNumber(area) || area === 0))
      errors.area = Translation("Area can only be a positive number");

    return errors;
  };

  const handleSubmitInfo = (values) => {
    setDetails((curr) => ({ ...curr, info: values }));
    goForward();
  };

  if (pageState !== PAGE_STATE.IDLE) {
    return <h1>Loading...</h1>;
  }

  return (
    <>
      <Formik
        initialValues={info}
        validate={handleValidate}
        onSubmit={handleSubmitInfo}
      >
        <Form className="new-project-info">
          <h1 className="new-project__heading1">
            {Translation("Share My Project")}
          </h1>
          <h3 className="new-project__heading3">
            {Translation("Information")}
          </h3>
          <TextBox
            name="name"
            placeholder={Translation("Name of project")}
            style={{ gridColumn: "1 / 3" }}
            maxLength={50}
          />
          <TextArea
            name="desc"
            placeholder={Translation("Description")}
            style={{ gridColumn: "1 / 3" }}
            maxLength={150}
          />
          <TextBox
            name="location"
            placeholder={"Location (Optional)"}
            style={{ gridColumn: "1 / 3" }}
            maxLength={30}
          />
          <TextBox
            name="price"
            type="number"
            placeholder={Translation("Price (Optional)")}
          />
          <TextBox
            name="area"
            type="number"
            placeholder={Translation("Size of area (sqm.) (Optional)")}
          />
          {details.type !== "images" && (
            <>
              <ComboBox
                name="program"
                options={[
                  {
                    name: "SketchUp",
                    value: "sketchUp",
                    icon: programSketchUpImage,
                  },
                  {
                    name: "3ds Max",
                    value: "3dsMax",
                    icon: program3dsMaxImage,
                  },
                  {
                    name: "Autodesk Revit",
                    value: "revit",
                    icon: programRevitImage,
                  },
                  {
                    name: "Cinema 4D",
                    value: "cinema4D",
                    icon: programCinema4DImage,
                  },
                  {
                    name: "Unreal",
                    value: "unreal",
                    icon: programUnrealImage,
                  },
                  {
                    name: "Rhinocerus",
                    value: "rhino",
                    icon: programRhinoImage,
                  },
                  {
                    name: "Autocad",
                    value: "autocad",
                    icon: programAutocadImage,
                  },
                  {
                    name: "Archicad",
                    value: "archicad",
                    icon: programArchicadImage,
                  },
                ]}
                placeholder={"Program used"}
              />
              <ComboBox
                name="renderer"
                options={[
                  {
                    name: "V-Ray",
                    value: "vray",
                    icon: programVRayImage,
                  },
                  {
                    name: "Corona",
                    value: "corona",
                    icon: programCoronaImage,
                  },
                  {
                    name: "Lumion",
                    value: "lumion",
                    icon: programLumionImage,
                  },
                  {
                    name: "Enscape",
                    value: "enscape",
                    icon: programEnscapeImage,
                  },
                  {
                    name: "D5render",
                    value: "d5render",
                    icon: programD5RenderImage,
                  },
                ]}
                placeholder={"Renderer"}
              />
            </>
          )}
          <h3 className="new-project__heading3">
            {Translation("Specify categories")}
          </h3>
          <ComboBox
            name="work"
            options={works}
            placeholder={Translation("Type of work")}
            // maxWidth={290}
            translation={TranslationWorkType}
          />
          <ComboBox
            name="space"
            options={spaces}
            placeholder={Translation("Type of spaces")}
            // maxWidth={290}
            translation={TranslationSpaceType}
          />
          <div style={{ gridColumn: "1 / 3" }}>
            <ComboBox
              name="style"
              options={styles}
              placeholder={Translation("Type of styles")}
              multi={true}
              // maxWidth={600}
              translation={TranslationStyleType}
            />
          </div>
          <div className="new-project-cta" style={{ gridColumn: "1 / 3" }}>
            <Button
              label={TranslationCommon("Return")}
              variant={"secondary"}
              leadingIcon={<ArrowLeftSvg />}
              onClick={() => handleGoBack()}
            />
            <Button
              label={TranslationCommon("Continue")}
              type="submit"
              trailingIcon={<ArrowRightSvg />}
            />
          </div>
        </Form>
      </Formik>
    </>
  );
};

Info.propTypes = {};

export default Info;
